// Core, side-effect-free weapon usability logic shared by helpers and components
// Inputs are plain data; outputs are a structured summary plus per-skill details.

export function evaluateWeaponUsability({
  staticSkills,
  characterSkills,
  itemType,
  strCur,
  dexCur,
}) {
  const characterSkillsSafe = Array.isArray(characterSkills) ? characterSkills : []
  const staticSkillsSafe = Array.isArray(staticSkills) ? staticSkills : []

  // --------------------------------------------------------------------------
  // aggregate min str/dex requirements across all linked weapon skills
  let minStrReq = null
  let minDexReq = null

  for (const s of staticSkillsSafe) {
    if (typeof s?.min_str === 'number') {
      minStrReq = minStrReq === null ? s.min_str : Math.max(minStrReq, s.min_str)
    }
    if (typeof s?.min_dex === 'number') {
      minDexReq = minDexReq === null ? s.min_dex : Math.max(minDexReq, s.min_dex)
    }
  }

  // --------------------------------------------------------------------------
  // match character skills by name and check str/dex requirements per skill

  const matchedSkillNames = []
  const totalSkills = staticSkillsSafe.length
  let fullyUsableCount = 0

  const missingSkillsOnly = []
  const missingSkillsAndStats = []
  const insufficientStatsSkills = []

  const perSkill = []

  if (staticSkillsSafe.length > 0) {
    for (const s of staticSkillsSafe) {
      if (!s) continue
      const skillName = s.name
      if (!skillName) continue

      const hasSkill = characterSkillsSafe.some((cs) => cs && cs.name === skillName)

      const reqStr = typeof s.min_str === 'number' ? s.min_str : null
      const reqDex = typeof s.min_dex === 'number' ? s.min_dex : null

      // evaluate stat requirements separately
      let statsOk = true
      if (itemType === 'Melee Weapon') {
        if (reqStr !== null && (strCur == null || strCur < reqStr)) {
          statsOk = false
        }
        if (reqDex !== null && (dexCur == null || dexCur < reqDex)) {
          statsOk = false
        }
      } else if (itemType === 'Ranged Weapon') {
        // for ranged weapons, treat both Str and Dex mins as requirements
        if (reqStr !== null && (strCur == null || strCur < reqStr)) {
          statsOk = false
        }
        if (reqDex !== null && (dexCur == null || dexCur < reqDex)) {
          statsOk = false
        }
      }

      const meets = !!hasSkill && !!statsOk

      if (hasSkill) {
        matchedSkillNames.push(skillName)

        if (!statsOk) {
          insufficientStatsSkills.push(skillName)
        }
      } else {
        if (!statsOk) {
          // missing the skill and also fails str/dex
          missingSkillsAndStats.push(skillName)
        } else {
          // missing the skill but str/dex are high enough
          missingSkillsOnly.push(skillName)
        }
      }

      if (meets) {
        fullyUsableCount += 1
      }

      perSkill.push({
        name: skillName,
        min_str: reqStr,
        min_dex: reqDex,
        hasSkill: !!hasSkill,
        statsOk: !!statsOk,
        meets: !!meets,
      })
    }
  }

  // --------------------------------------------------------------------------
  // determine usable_status:
  // - 'No' when no required skill+stat combo is fully met
  // - 'Yes' when all required skills are fully usable
  // - 'Partial' when some but not all required skills are fully usable

  let usable_status = 'No'

  if (fullyUsableCount === 0) {
    usable_status = 'No'
  } else if (fullyUsableCount === totalSkills) {
    usable_status = 'Yes'
  } else {
    usable_status = 'Partial'
  }

  // ----------------------------------------------------------------------
  // build unusable_reasons text explaining why weapon is not fully usable
  // ----------------------------------------------------------------------

  let unusable_reasons = ''

  if (totalSkills === 0) {
    unusable_reasons = 'No linked weapon skills'
  } else if (usable_status === 'Yes') {
    unusable_reasons = 'n/a'
  } else {
    if (matchedSkillNames.length === 0) {
      // character has none of the required skills
      const allSkillNames = staticSkillsSafe.map((s) => s && s.name).filter(Boolean)

      const lines = []
      lines.push(`No matching skills: ${allSkillNames.join(' / ')}`)

      // show per-skill str/dex requirements so mixed requirements (e.g. 1H vs 2H)
      // are clear instead of a single aggregated "at least" summary
      const skillReqs = staticSkillsSafe
        .map((s) => {
          if (!s) return null
          const name = s.name
          if (!name) return null
          const parts = []
          if (typeof s.min_str === 'number') parts.push(`Str ${s.min_str}`)
          if (typeof s.min_dex === 'number') parts.push(`Dex ${s.min_dex}`)

          // base label with requirements, if any
          const baseLabel = parts.length ? `${name} (${parts.join(' / ')})` : name

          // use precomputed buckets to indicate whether current Str/Dex meet the reqs
          let statusSuffix = ''
          if (missingSkillsAndStats.includes(name)) {
            statusSuffix = ': not met'
          } else if (missingSkillsOnly.includes(name)) {
            statusSuffix = ': met'
          }

          return `${baseLabel}${statusSuffix}`
        })
        .filter(Boolean)

      if (skillReqs.length) {
        lines.push('Skill requirements:')
        lines.push(...skillReqs)
      }

      unusable_reasons = lines.join('\n')
    } else {
      const reasons = []

      if (missingSkillsOnly.length > 0) {
        reasons.push(`Missing skill(s): ${missingSkillsOnly.join(' / ')}`)
      }
      if (missingSkillsAndStats.length > 0) {
        // put skill list on a new line after the label
        reasons.push(
          `Missing skill(s) & insufficient Str/Dex:\n${missingSkillsAndStats.join(' / ')}`,
        )
      }
      if (insufficientStatsSkills.length > 0) {
        reasons.push(`Insufficient Str/Dex for skill(s): ${insufficientStatsSkills.join(' / ')}`)
      }

      // only show the aggregated min Str/Dex when the character
      // actually has at least one matching skill
      const reqParts = []
      if (minStrReq !== null) reqParts.push(`Str ${minStrReq}`)
      if (minDexReq !== null) reqParts.push(`Dex ${minDexReq}`)
      if (reqParts.length > 0) {
        reasons.push(`Requires at least ${reqParts.join(' / ')}`)
      }

      unusable_reasons = reasons.join('\n')
    }
  }

  return {
    minStrReq,
    minDexReq,
    matchedSkillNames,
    totalSkills,
    fullyUsableCount,
    missingSkillsOnly,
    missingSkillsAndStats,
    insufficientStatsSkills,
    usable_status,
    unusable_reasons,
    perSkill,
  }
}
